import { useMemo, type ReactNode } from "react";
import { Film, FileOutput, Image as ImageIcon } from "lucide-react";
import { cn } from "@/utils/helpers";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";
import {
  isStaticBody,
  type CalendarBriefKind,
  type ProductionBriefBody,
  type ProductionMeta,
} from "@/types/productionBrief.type";
import { BriefStaticBody, BriefVideoBody } from "./brief/BriefBodies";
import { briefAccentColor, type BriefAccent, type BriefAudience } from "./brief/BriefPrimitives";


function DocStatus({ kind }: { kind: CalendarBriefKind }) {
  return (
    <div className="rounded-card bg-co-accent-dim px-4 py-3.5">
      <p className="text-[12.5px] text-co-accent-ink">Production status</p>
      <p className="mt-1.5 font-section text-[18px] font-bold leading-none text-co-text2">Ready for execution</p>
      <p className="mt-1.5 text-[12.5px] text-co-muted">
        {kind === "STATIC" ? "Static design brief" : "Video production brief"}
      </p>
    </div>
  );
}

function ProductionMetaBand({ meta }: { meta?: ProductionMeta }) {
  if (!meta || !Object.values(meta).some(Boolean)) return null;
  const entries = [
    ["Owner", meta.owner],
    ["Production lead", meta.productionLead],
    ["Production date", meta.productionDate],
    ["Internal review", meta.internalReviewDate],
    ["Client due", meta.clientDueDate],
  ].filter(([, value]) => value);
  return (
    <dl className="grid border-y border-co-line @[32rem]/briefdoc:grid-cols-2 @[58rem]/briefdoc:grid-cols-5">
      {entries.map(([label, value]) => (
        <div key={label} className="py-2.5 @[32rem]/briefdoc:border-r @[32rem]/briefdoc:border-co-line @[32rem]/briefdoc:px-3 @[32rem]/briefdoc:first:pl-0 @[32rem]/briefdoc:last:border-r-0">
          <dt className="text-[12.5px] text-co-muted">{label}</dt>
          <dd className="mt-1 text-[12px] font-medium leading-snug text-co-text2">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const LINE = "rounded-tag bg-co-surface-solid motion-safe:animate-pulse";

function SkeletonShot({ n }: { n: string }) {
  return (
    <div className="flex items-center gap-4 py-2.5 shadow-[inset_0_1px_0_var(--co-line)]">
      <span className="w-7 shrink-0 font-section text-[18px] font-bold text-co-muted">{n}</span>
      <span className="grid h-[54px] w-24 shrink-0 place-items-center rounded-card bg-co-surface">
        <ImageIcon className="size-4 text-co-faint" strokeWidth={1.6} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className={cn(LINE, "h-2.5 w-full")} />
        <span className={cn(LINE, "h-2.5 w-[300px] max-w-full")} />
      </span>
      <span className="hidden w-[260px] shrink-0 flex-col gap-2 @[46rem]/briefskel:flex">
        <span className={cn(LINE, "h-[9px] w-[200px]")} />
        <span className={cn(LINE, "h-[9px] w-[150px]")} />
      </span>
    </div>
  );
}

function SkeletonPhoneShot() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="h-10 w-16 shrink-0 rounded-tag bg-co-surface" />
      <span className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <span className={cn(LINE, "h-[9px] w-full")} />
        <span className={cn(LINE, "h-[9px] w-40")} />
      </span>
    </div>
  );
}

/** A skeleton section: the tag in its colour and the rule down the left stay still; only the bars pulse. */
function SkeletonSection({ title, accent, children }: { title: string; accent: BriefAccent; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-l-[3px] pl-4" style={{ borderLeftColor: briefAccentColor(accent) }}>
      <span
        className="inline-flex w-fit rounded-[6px] px-2.5 py-0.5 font-section text-[17px] font-bold leading-[22px] text-co-text @[600px]/briefskel:text-[20px] @[600px]/briefskel:leading-[24px]"
        style={{ background: `color-mix(in srgb, ${briefAccentColor(accent)} 28%, transparent)` }}
      >
        {title}
      </span>
      {children}
    </div>
  );
}

/**
 * Calendar / Brief skeleton (and · Phone): the brief while Creo writes it,
 * shaped like the brief (Figma 08, "the brief being written"): Treatment with
 * the hook card beside its lines, the shot rows, Production inputs and
 * Voiceover, script and sound. Under 600 wide it is the phone's stacked form.
 */
export function ProductionBriefSkeleton() {
  return (
    <div className="@container/briefskel w-full" aria-hidden="true">
      <div className="hidden flex-col gap-9 @[600px]/briefskel:flex">
        <SkeletonSection title="Treatment" accent="blue">
          <div className="flex gap-6">
            <div className="flex w-[360px] max-w-[45%] shrink-0 flex-col gap-2 rounded-card bg-co-surface px-3.5 py-3">
              <p className="text-[12px] text-co-muted">Opening hook</p>
              <span className={cn(LINE, "h-3 w-full")} />
              <span className={cn(LINE, "h-3 w-[220px] max-w-full")} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-[9px] py-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={cn(LINE, "h-[9px] w-full")} />
              ))}
            </div>
          </div>
        </SkeletonSection>
        <SkeletonSection title="Shot list" accent="indigo">
          <div>
            {["01", "02", "03"].map((n) => (
              <SkeletonShot key={n} n={n} />
            ))}
          </div>
        </SkeletonSection>
        <SkeletonSection title="Production inputs" accent="purple">
          <div className="grid grid-cols-3 gap-6">
            {["Props and products", "Wardrobe", "Locations"].map((label) => (
              <div key={label} className="flex flex-col gap-2">
                <p className="text-[12.5px] text-co-muted">{label}</p>
                <span className={cn(LINE, "h-[9px] w-full")} />
                <span className={cn(LINE, "h-[9px] w-40 max-w-full")} />
              </div>
            ))}
          </div>
        </SkeletonSection>
        <SkeletonSection title="Voiceover, script and sound" accent="amber">
          <span className={cn(LINE, "h-[9px] w-full")} />
          <span className={cn(LINE, "h-[9px] w-[520px] max-w-full")} />
        </SkeletonSection>
      </div>
      <div className="flex flex-col gap-6 @[600px]/briefskel:hidden">
        <SkeletonSection title="Treatment" accent="blue">
          <span className={cn(LINE, "h-[9px] w-full")} />
          <span className={cn(LINE, "h-[9px] w-[220px] max-w-full")} />
          <span className={cn(LINE, "h-[9px] w-full")} />
        </SkeletonSection>
        <SkeletonSection title="Shot list" accent="indigo">
          <SkeletonPhoneShot />
          <SkeletonPhoneShot />
        </SkeletonSection>
        <SkeletonSection title="Production inputs" accent="purple">
          <span className={cn(LINE, "h-[9px] w-full")} />
          <span className={cn(LINE, "h-[9px] w-[220px] max-w-full")} />
        </SkeletonSection>
      </div>
    </div>
  );
}

export default function ProductionBriefDoc({
  kind,
  body: rawBody,
  className,
  showSynopsis = true,
  showHeader = true,
  askedSeconds,
  aspectRatio,
  audience = "team",
  brandName,
  onChooseVisual,
}: {
  kind: CalendarBriefKind;
  body: ProductionBriefBody;
  className?: string;
  showSynopsis?: boolean;
  showHeader?: boolean;
  /** Kept for callers; the calm read view draws no pillar colour. */
  pillarHue?: string;
  /** The idea's length in seconds, for the Length sentence and the timeline's over/under. */
  askedSeconds?: number | null;
  /** The post's frame; the video brief's shot pictures follow it. */
  aspectRatio?: string | null;
  audience?: BriefAudience;
  /** The brand, for the video Length line's "tell {brand}" when the cut runs over. */
  brandName?: string | null;
  /**
   * Present when the reader can save a pick: an empty shot or slide shows the
   * visual picker instead of its placeholder. Called with the row's index.
   */
  onChooseVisual?: (index: number) => void;
}) {
  const body = useMemo(() => scrubVendorTermsDeep(rawBody), [rawBody]);
  const hasMeta = !!body.productionMeta && Object.values(body.productionMeta).some(Boolean);
  return (
    <article className={cn("@container/briefdoc flex w-full min-w-0 flex-col gap-11", className)}>
      {showHeader ? <header className="pb-5 shadow-[inset_0_-1px_0_var(--co-line)]">
        <div className="mb-5 flex items-center gap-3">
          <span className="text-[12.5px] font-semibold text-co-accent-ink">Mohtawa</span>
          <span className="h-px w-8 bg-co-accent" aria-hidden="true" />
          <span className="inline-flex items-center gap-2 text-[12.5px] text-co-muted">
            {kind === "STATIC" ? <FileOutput className="size-3.5" /> : <Film className="size-3.5" />}
            Production document
          </span>
        </div>
        <div className="grid gap-6 @[46rem]/briefdoc:grid-cols-[minmax(0,1fr)_15rem] @[46rem]/briefdoc:items-start">
          <div>
            <h2 className="max-w-[22ch] font-section text-[clamp(2.25rem,5vw,3.75rem)] font-bold leading-[1] text-co-text">{body.title}</h2>
            {showSynopsis && body.synopsis ? <p className="mt-3 max-w-[72ch] text-[13px] leading-relaxed text-co-muted">{body.synopsis}</p> : null}
          </div>
          <DocStatus kind={kind} />
        </div>
        {hasMeta ? <div className="mt-4"><ProductionMetaBand meta={body.productionMeta} /></div> : null}
      </header> : null}
      {/* Without the header the page around the brief (the date rail and the
          banner) holds the dates, so the meta band is not repeated here. */}
      {isStaticBody(kind, body) ? (
        <BriefStaticBody body={body} audience={audience} onChooseVisual={onChooseVisual} />
      ) : (
        <BriefVideoBody
          body={body}
          askedSeconds={askedSeconds}
          aspectRatio={aspectRatio}
          audience={audience}
          brandName={brandName}
          onChooseVisual={onChooseVisual}
        />
      )}
    </article>
  );
}
